import CustomButton from "@/components/common/Button";
import { ImportRecord } from "@/utils/types/importManagement";
import { Alert, Progress, Tag, Typography } from "antd";
import { Settings } from "lucide-react";
import {
  JSXElementConstructor,
  ReactElement,
  ReactNode,
  ReactPortal,
} from "react";

const { Text } = Typography;

interface PendingUIProps {
  importDetail: ImportRecord;
  dbColumns: { label: string; value: string }[];
  mappedColumns: number;
  totalColumns: number;
  onConfigureMapping: () => void;
  disabled?: boolean;
}

const PendingUI = ({
  importDetail,
  dbColumns,
  mappedColumns,
  totalColumns,
  onConfigureMapping,
  disabled = false,
}: PendingUIProps) => {
  return (
    <div className="space-y-4">
      <Alert
        type="info"
        message="Column mapping required"
        description={`${mappedColumns} of ${totalColumns} database fields are mapped. Configure mapping to proceed.`}
        showIcon
      />

      {mappedColumns < totalColumns && (
        <div className="mb-4">
          <Text strong>Pending database fields:</Text>
          <div className="mt-2 flex flex-wrap gap-2">
            {dbColumns
              .filter((dbColumn: { value: string | null }) => {
                return Array.isArray(importDetail.column_mapping)
                  ? !importDetail.column_mapping.some(
                      mapping => mapping.db_column_name === dbColumn.value
                    )
                  : true;
              })
              .map(
                (dbColumn: {
                  label:
                    | string
                    | number
                    | boolean
                    | ReactElement<any, string | JSXElementConstructor<any>>
                    | Iterable<ReactNode>
                    | ReactPortal
                    | Iterable<ReactNode>
                    | null
                    | undefined;
                  value:
                    | string
                    | number
                    | boolean
                    | ReactElement<any, string | JSXElementConstructor<any>>
                    | Iterable<ReactNode>
                    | ReactPortal
                    | Iterable<ReactNode>
                    | null
                    | undefined;
                }) => (
                  <Tag key={dbColumn.label as string} color="orange">
                    {dbColumn.label} ({dbColumn.value})
                  </Tag>
                )
              )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <Progress
          percent={Math.round((mappedColumns / totalColumns) * 100)}
          status={mappedColumns === totalColumns ? "success" : "active"}
          format={() =>
            `${mappedColumns}/${totalColumns} database fields mapped`
          }
        />

        <div className="flex gap-2">
          <CustomButton
            icon={<Settings size={14} />}
            onClick={onConfigureMapping}
            disabled={disabled}
            title="Configure Column Mapping"
          >
            Configure Mapping
          </CustomButton>
        </div>
      </div>
    </div>
  );
};

export default PendingUI;
